import { OptionStrategy } from '#/shared/strategies';
import { DraftOptionLeg } from './types';

export function checkStrategyLegExpirationPolicy(
  { expirationValidationPolicy }: OptionStrategy,
  legs: readonly DraftOptionLeg[],
) {
  switch (expirationValidationPolicy) {
    case 'asc':
      return checkLegExpirationOrdered(legs, 'asc');
    case 'none':
      return true;
    // no default
  }
}

function checkLegExpirationOrdered(
  legs: readonly DraftOptionLeg[],
  order: 'asc' | 'desc',
) {
  const legsExpirations = processLegsExpirations(legs);
  return legsExpirations.every((curLegExpiration, index) => {
    if (index === 0) return true;
    const prevLegExpiration = legsExpirations[index - 1]!;

    return order === 'asc'
      ? prevLegExpiration < curLegExpiration
      : prevLegExpiration > curLegExpiration;
  });
}

const processLegsExpirations = (legs: readonly DraftOptionLeg[]) =>
  legs
    .map((leg) => (leg.expiration != null ? leg.expiration : NaN))
    .filter(Number.isFinite);

export function checkStrategyLegStrikePolicy(
  { strikeValidationPolicy, validateStrikes }: OptionStrategy,
  legs: readonly DraftOptionLeg[],
) {
  switch (strikeValidationPolicy) {
    case 'asc':
      return checkLegStrikeOrdered(legs, 'asc');
    case 'desc':
      return checkLegStrikeOrdered(legs, 'desc');
    case 'same':
      return checkLegStrikeAreTheSame(legs);
    case 'diff':
      return checkLegStrikeAreDifferent(legs);
    case 'custom':
      if (validateStrikes == null)
        throw new TypeError('A custom validation function is required');
      return validateStrikes(legs);
    case 'none':
      return true;
    // no default
  }
}

function checkLegStrikeAreTheSame(legs: readonly DraftOptionLeg[]) {
  const strikes = processLegsStrikes(legs);
  const uniqueStrikes = new Set(strikes);
  return uniqueStrikes.size === 1;
}

function checkLegStrikeAreDifferent(legs: readonly DraftOptionLeg[]) {
  const strikes = processLegsStrikes(legs);
  const uniqueStrikes = new Set(strikes);
  return uniqueStrikes.size === strikes.length;
}

function checkLegStrikeOrdered(
  legs: readonly DraftOptionLeg[],
  order: 'desc' | 'asc',
) {
  const legsStrikes = processLegsStrikes(legs);
  return legsStrikes.every((curLegStrike, index) => {
    if (index === 0) return true;
    const prevLegStrike = legsStrikes[index - 1]!;

    return order === 'asc'
      ? prevLegStrike < curLegStrike
      : prevLegStrike > curLegStrike;
  });
}

export const processLegsStrikes = (legs: readonly DraftOptionLeg[]) =>
  legs
    .map((leg) => (leg.strike != null ? Number.parseFloat(leg.strike) : NaN))
    .filter(Number.isFinite);
